// WebSocket连接管理工具
import { getToken } from './api';

// 获取WebSocket基础URL
function getWebSocketBaseURL(): string {
  // 优先使用环境变量
  if (process.env.NEXT_PUBLIC_WS_URL) {
    return process.env.NEXT_PUBLIC_WS_URL;
  }
  
  // 如果没有环境变量，从API_BASE_URL推导
  const apiUrl = process.env.NEXT_PUBLIC_API_URL || '/api';
  
  if (typeof window !== 'undefined') {
    // 如果是代理路径（/api），需要连接到后端服务器
    // 开发环境通常后端在8080端口
    if (apiUrl.startsWith('/')) {
      // 开发环境：尝试连接到 localhost:8080
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return 'ws://localhost:8080';
      }
      // 生产环境：使用当前窗口的host
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      return `${protocol}//${host}`;
    } else {
      // 如果是完整URL，转换为WebSocket URL
      const url = new URL(apiUrl);
      const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${protocol}//${url.host}`;
    }
  }
  
  return '';
}

const WS_BASE_URL = getWebSocketBaseURL();

export interface WSMessage {
  type: string;
  data: unknown;
}

type MessageHandler = (message: WSMessage) => void;
type ConnectionHandler = () => void;

class WebSocketManager {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000; // 3秒
  private reconnectTimer: NodeJS.Timeout | null = null;
  private messageHandlers: Set<MessageHandler> = new Set();
  private connectionHandlers: Set<ConnectionHandler> = new Set();
  private disconnectHandlers: Set<ConnectionHandler> = new Set();
  private isConnecting = false;
  private isManualClose = false;

  // 连接WebSocket
  connect(): boolean {
    // 如果正在连接或已连接，不重复连接
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      return false;
    }

    // 如果没有token，无法连接
    const token = getToken();
    if (!token) {
      console.warn('WebSocket: No token available, cannot connect');
      return false;
    }

    this.isConnecting = true;
    this.isManualClose = false;

    try {
      // 构建WebSocket URL，包含token
      const wsUrl = `${WS_BASE_URL}/ws/chat?token=${encodeURIComponent(token)}`;
      
      console.log('WebSocket: Connecting to', wsUrl.replace(/token=[^&]+/, 'token=***'));
      
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('WebSocket: Connected successfully');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        
        // 通知所有连接处理器
        this.connectionHandlers.forEach(handler => {
          try {
            handler();
          } catch (error) {
            console.error('WebSocket: Error in connection handler', error);
          }
        });
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WSMessage;
          console.log('WebSocket: Received message', message.type);
          
          // 通知所有消息处理器
          this.messageHandlers.forEach(handler => {
            try {
              handler(message);
            } catch (error) {
              console.error('WebSocket: Error in message handler', error);
            }
          });
        } catch (error) {
          console.error('WebSocket: Error parsing message', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket: Connection error', error);
        this.isConnecting = false;
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket: Connection closed', event.code, event.reason);
        this.isConnecting = false;
        this.ws = null;

        // 通知所有断开连接处理器
        this.disconnectHandlers.forEach(handler => {
          try {
            handler();
          } catch (error) {
            console.error('WebSocket: Error in disconnect handler', error);
          }
        });

        // 如果不是手动关闭，尝试重连
        if (!this.isManualClose && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          console.log(`WebSocket: Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
          
          this.reconnectTimer = setTimeout(() => {
            this.connect();
          }, this.reconnectDelay);
        } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          console.error('WebSocket: Max reconnection attempts reached');
        }
      };

      return true;
    } catch (error) {
      console.error('WebSocket: Failed to create connection', error);
      this.isConnecting = false;
      return false;
    }
  }

  // 断开连接
  disconnect(): void {
    this.isManualClose = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  // 发送消息
  send(message: WSMessage): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket: Cannot send message, connection not open');
      return false;
    }

    try {
      this.ws.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error('WebSocket: Error sending message', error);
      return false;
    }
  }

  // 添加消息处理器
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    // 返回取消订阅函数
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  // 添加连接处理器
  onConnect(handler: ConnectionHandler): () => void {
    this.connectionHandlers.add(handler);
    return () => {
      this.connectionHandlers.delete(handler);
    };
  }

  // 添加断开连接处理器
  onDisconnect(handler: ConnectionHandler): () => void {
    this.disconnectHandlers.add(handler);
    return () => {
      this.disconnectHandlers.delete(handler);
    };
  }

  // 获取连接状态
  getState(): 'connecting' | 'open' | 'closing' | 'closed' {
    if (!this.ws) return 'closed';
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'open';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'closed';
      default:
        return 'closed';
    }
  }

  // 检查是否已连接
  isConnected(): boolean {
    return this.getState() === 'open';
  }
}

// 创建全局WebSocket管理器实例
export const wsManager = new WebSocketManager();

// 初始化WebSocket连接（在用户登录后调用）
export function initWebSocket(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  // 如果已经有token，尝试连接
  const token = getToken();
  if (token) {
    return wsManager.connect();
  }

  return false;
}

// 断开WebSocket连接（在用户登出时调用）
export function closeWebSocket(): void {
  wsManager.disconnect();
}

